import { Component } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { CardContent, Paper, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material"
import { DATE } from "helpers/shapes"
import { numberFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"
import { computeCSVData } from "helpers/csv"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import HighestLowestAttribution from "components/highest-lowest-attribution"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import DetailRow from "./components/detail-row"

export default class SecurityProfileExposure extends Component {
  static propTypes = {
    earliestDate: DATE,
    latestDate: DATE,
    startDate: DATE,
    endDate: DATE,
    selectedDate: DATE,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    displayType: PropTypes.string.isRequired,
    setDate: PropTypes.func.isRequired,
    setDisplayType: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    isPeriodIntraday: PropTypes.bool,
    firstDate: PropTypes.string.isRequired,
    resetDate: PropTypes.func.isRequired,
    data: PropTypes.array.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    details: PropTypes.array.isRequired,
    openFactorProfile: PropTypes.func.isRequired,
    exposureAttribution: PropTypes.object.isRequired,
    maxFactors: PropTypes.number.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    trackCSVDownload: PropTypes.func.isRequired,
    fileName: PropTypes.string.isRequired,
  }

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate } = this.props
    const startDate = firstDate || propsStartDate

    if (selectedDate && (selectedDate < startDate)) {
      this.props.resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setDisplayType } = this.props
    setDisplayType(id)
  }

  render() {
    const {
      setDate,
      selectedDate,
      startDate,
      endDate,
      items,
      displayType,
      options,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      data,
      details,
      openFactorProfile,
      exposureAttribution,
      maxFactors,
      toggleOnly,
      only,
      fileName,
      trackCSVDownload,
      isPeriodIntraday,
    } = this.props

    return <CardContent className="card-content">
      <Paper className="content">
        <div className="graph">
          <GraphTopBar {...{
            value: displayType,
            onTabChanged: this.onTabChanged,
            tabs: map(options, ({ id, label, value }) => ({
              key: id,
              value: id,
              label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
            })),
            actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items)} fileName={fileName} track={trackCSVDownload} />],
          }}
          />
          <MultiLine
            tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={numberFormatter} isOneDayMode={isPeriodIntraday} />}
            {...{
              startDate,
              date: selectedDate,
              setDate,
              endDate,
              data,
              onlyItem: only,
              items,
              toggle,
              focusChartLine,
              blurChartLine,
              focusedChartLine,
              toggleOnly,
              isPeriodIntraday,
              isOneDayMode: isPeriodIntraday,
            }}
          />
        </div>
        <div className="details">
          <h4 className="header">As of {formatLongDate(selectedDate)}</h4>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="75%" />
                <TableCell align="right">Exposure</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(details, (value, index) => {
                if (displayType === "total" && index === 0) {
                  return null
                } else {
                  return <DetailRow key={value.id} value={value} index={index} openFactorProfile={openFactorProfile} />
                }
              })}
            </TableBody>
          </Table>
        </div>
      </Paper>
      <HighestLowestAttribution
        date={selectedDate}
        noPercent
        attribution={{
          category: "Biggest Exposures",
          factors: exposureAttribution,
        }}
        maxFactors={maxFactors}
        openFactorProfile={openFactorProfile}
      />
    </CardContent>
  }
}
